/* PALISADE CUSTOM — announcement bar behavior. */
import { Component } from '@theme/component';

/**
 * Wrapper around Horizon's `announcement-bar-component` that adds:
 * - Dismiss: hides the section for the rest of the browsing session.
 *   The sessionStorage key is derived from the message text (see the
 *   section file), so editing the announcement re-surfaces the bar.
 * - Reduced motion: pauses autoplay when `prefers-reduced-motion: reduce`
 *   matches. The previous/next arrows remain for manual navigation.
 *
 * @extends {Component}
 */
class PalisadeAnnouncementBar extends Component {
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.#applyMotionPreference();
    this.#motionQuery.addEventListener('change', this.#applyMotionPreference);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#motionQuery.removeEventListener('change', this.#applyMotionPreference);
  }

  /**
   * Pauses the inner slider for users who prefer reduced motion,
   * and resumes it when the preference is lifted.
   */
  #applyMotionPreference = () => {
    customElements.whenDefined('announcement-bar-component').then(() => {
      const slider = this.querySelector('announcement-bar-component');

      if (!slider || typeof (/** @type {any} */ (slider).pause) !== 'function') return;

      const sliderComponent = /** @type {any} */ (slider);

      if (this.#motionQuery.matches) {
        sliderComponent.pause();
      } else if (sliderComponent.paused) {
        sliderComponent.paused = false;
        sliderComponent.play();
      }
    });
  };

  /**
   * Hides the announcement section and remembers the choice for this session.
   * Invoked declaratively via `on:click="/dismiss"`.
   */
  dismiss() {
    const wrapper = this.closest('.shopify-section') ?? this;

    if (wrapper instanceof HTMLElement) wrapper.style.display = 'none';

    // Don't persist dismissals made inside the theme editor preview.
    if (window.Shopify?.designMode) return;

    try {
      const key = this.dataset.storageKey;
      if (key) sessionStorage.setItem(key, '1');
    } catch {
      // Storage unavailable (e.g. blocked third-party context) — the bar
      // simply reappears on the next page view.
    }
  }
}

if (!customElements.get('palisade-announcement-bar-component')) {
  customElements.define('palisade-announcement-bar-component', PalisadeAnnouncementBar);
}
